import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTheme } from '@mui/material/styles';
import { getWorkspaceTokens } from './workspaceStyles';

const DENSITY_STYLES = {
  compact: {
    mb: 0.5,
    py: 0.25,
    titleSize: 14,
    descriptionSize: 11,
  },
  comfortable: {
    mb: 0.75,
    py: 0.5,
    titleSize: 15,
    descriptionSize: 12,
  },
};

function AnalyticsObjectHeader({
  title,
  description,
  eyebrow,
  icon: Icon,
  actions,
  filters,
  hideable = false,
  hidden = false,
  onHiddenChange,
  hideLabel = 'Hide',
  showLabel = 'Show',
  density = 'compact',
  variant = 'panel',
  width = '100%',
  maxWidth = '100%',
  contentWidth = '100%',
  contentMaxWidth = '100%',
  contentAlign = 'stretch',
  sx,
}) {
  const theme = useTheme();
  const tokens = getWorkspaceTokens(theme);
  const densityStyles = DENSITY_STYLES[density] || DENSITY_STYLES.compact;
  const ToggleIcon = hidden ? VisibilityIcon : VisibilityOffIcon;
  const toggleLabel = hidden ? showLabel : hideLabel;
  const showSecondaryRow = Boolean(filters);

  return (
    <Box
      data-testid="analytics-object-header"
      data-variant={variant}
      sx={{
        width,
        maxWidth,
        display: 'flex',
        flexDirection: 'column',
        mb: densityStyles.mb,
        py: densityStyles.py,
        px: 0,
        minWidth: 0,
        ...sx,
      }}
    >
      <Box
        data-testid="analytics-object-header-content"
        sx={{
          width: contentWidth,
          maxWidth: contentMaxWidth,
          alignSelf: contentAlign,
          display: 'flex',
          flexDirection: 'column',
          gap: showSecondaryRow ? 0.5 : 0,
          minWidth: 0,
        }}
      >
        <Box
          data-testid="analytics-object-header-primary-row"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
            minWidth: 0,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              minWidth: 0,
              maxWidth: '100%',
              flex: '0 1 auto',
            }}
          >
            {Icon && (
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: `${tokens.radius}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.palette.primary.main,
                  backgroundColor: tokens.muted,
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: 15 }} />
              </Box>
            )}
            <Box
              sx={{ minWidth: 0 }}
            >
              {eyebrow && (
                <Typography
                  sx={{
                    color: theme.palette.text.disabled,
                    fontSize: 10,
                    fontWeight: 700,
                    lineHeight: 1.1,
                    textTransform: 'uppercase',
                  }}
                >
                  {eyebrow}
                </Typography>
              )}
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: densityStyles.titleSize,
                  fontWeight: 650,
                  lineHeight: 1.18,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </Typography>
              {description && (
                <Typography
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: densityStyles.descriptionSize,
                    lineHeight: 1.25,
                    opacity: 0.82,
                  }}
                >
                  {description}
                </Typography>
              )}
            </Box>
          </Box>

          {(actions || hideable) && (
            <Box
              data-testid="analytics-object-header-actions"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 0.75,
                minWidth: 0,
                maxWidth: '100%',
                flex: '1 1 220px',
                flexWrap: 'wrap',
                overflow: 'visible',
                '& > *': { flex: '0 0 auto' },
              }}
            >
              {actions}
              {hideable && (
                <Button
                  size="small"
                  variant={hidden ? 'outlined' : 'text'}
                  startIcon={<ToggleIcon fontSize="small" />}
                  onClick={() => onHiddenChange?.(!hidden)}
                  aria-pressed={hidden}
                  sx={{
                    minHeight: tokens.controlHeight - 4,
                    height: tokens.controlHeight - 4,
                    px: 0.75,
                    textTransform: 'none',
                    fontSize: 11,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    color: hidden ? theme.palette.primary.main : theme.palette.text.secondary,
                  }}
                >
                  {toggleLabel}
                </Button>
              )}
            </Box>
          )}
        </Box>

        {showSecondaryRow && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              alignItems: 'center',
              gap: 0.75,
              minWidth: 0,
              maxWidth: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              '& > *': { flex: '0 0 auto' },
            }}
          >
            {filters}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default AnalyticsObjectHeader;
